import {useEffect, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {FormattedMessage, useIntl} from 'react-intl'
import type {IntlShape, MessageDescriptor} from 'react-intl'
import {SECURITY_MESSAGES as S} from './securityMessages'
import {
  CONFIRM_TWO_FACTOR_SETUP,
  DISABLE_TWO_FACTOR,
  START_TWO_FACTOR_SETUP,
} from '../../gql/mutations/authMutations'
import {
  RESET_USER_TWO_FACTOR,
  SET_USER_TWO_FACTOR_EMAIL_FALLBACK,
  SET_USER_TWO_FACTOR_REQUIRED,
} from '../../gql/mutations/userMutations'
import {
  IResConfirmTwoFactorSetup,
  IResDisableTwoFactor,
  IResStartTwoFactorSetup,
} from '../../types/auth'

type ResetTwoFactorResponse = {
  resetUserTwoFactorMutation: {
    success: boolean
    twoFactorEnabled?: boolean | null
    twoFactorEmailFallbackEnabled?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

type SetUserTwoFactorEmailFallbackResponse = {
  setUserTwoFactorEmailFallbackMutation: {
    success: boolean
    twoFactorEmailFallbackEnabled?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

type SetUserTwoFactorRequiredResponse = {
  setUserTwoFactorRequiredMutation: {
    success: boolean
    twoFactorRequired?: boolean | null
    errors?: {field?: string | null; message: string}[] | null
  }
}

interface TwoFactorSettingsPanelProps {
  twoFactorEnabled: boolean
  twoFactorRequired?: boolean
  twoFactorEmailFallbackEnabled?: boolean
  canManageOwnTwoFactor?: boolean
  canResetTwoFactor?: boolean
  showAdminRequiredControl?: boolean
  targetUserId?: string | number | null
  onUpdated?: () => void | Promise<unknown>
}

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.graphQLErrors?.[0]?.message) return error.graphQLErrors[0].message
  if (typeof error?.message === 'string' && error.message.trim()) return error.message
  return fallback
}

export const TwoFactorSettingsPanel = ({
  twoFactorEnabled,
  twoFactorRequired = false,
  twoFactorEmailFallbackEnabled = false,
  canManageOwnTwoFactor = false,
  canResetTwoFactor = false,
  showAdminRequiredControl = true,
  targetUserId,
  onUpdated,
}: TwoFactorSettingsPanelProps) => {
  const intl = useIntl()
  const [twoFactorSetupKey, setTwoFactorSetupKey] = useState<string | null>(null)
  const [twoFactorSetupUrl, setTwoFactorSetupUrl] = useState<string | null>(null)
  const [twoFactorSetupQrSvg, setTwoFactorSetupQrSvg] = useState<string | null>(null)
  const [twoFactorSetupCode, setTwoFactorSetupCode] = useState('')
  const [disableTwoFactorPassword, setDisableTwoFactorPassword] = useState('')
  const [disableTwoFactorCode, setDisableTwoFactorCode] = useState('')
  const [adminTwoFactorRequired, setAdminTwoFactorRequired] = useState(Boolean(twoFactorRequired))
  const [adminEmailFallbackEnabled, setAdminEmailFallbackEnabled] = useState(
    Boolean(twoFactorEmailFallbackEnabled)
  )

  const [gqlStartTwoFactorSetup, {loading: loadingStartTwoFactorSetup}] =
    useMutation<IResStartTwoFactorSetup>(START_TWO_FACTOR_SETUP)
  const [gqlConfirmTwoFactorSetup, {loading: loadingConfirmTwoFactorSetup}] =
    useMutation<IResConfirmTwoFactorSetup>(CONFIRM_TWO_FACTOR_SETUP)
  const [gqlDisableTwoFactor, {loading: loadingDisableTwoFactor}] =
    useMutation<IResDisableTwoFactor>(DISABLE_TWO_FACTOR)
  const [gqlResetTwoFactor, {loading: loadingResetTwoFactor}] =
    useMutation<ResetTwoFactorResponse>(RESET_USER_TWO_FACTOR)
  const [gqlSetUserTwoFactorRequired, {loading: loadingSetUserTwoFactorRequired}] =
    useMutation<SetUserTwoFactorRequiredResponse>(SET_USER_TWO_FACTOR_REQUIRED)
  const [gqlSetUserTwoFactorEmailFallback, {loading: loadingSetUserTwoFactorEmailFallback}] =
    useMutation<SetUserTwoFactorEmailFallbackResponse>(SET_USER_TWO_FACTOR_EMAIL_FALLBACK)

  useEffect(() => {
    setAdminTwoFactorRequired(Boolean(twoFactorRequired))
  }, [twoFactorRequired])

  useEffect(() => {
    setAdminEmailFallbackEnabled(Boolean(twoFactorEmailFallbackEnabled))
  }, [twoFactorEmailFallbackEnabled])

  const runUpdated = async () => {
    if (onUpdated) {
      await Promise.resolve(onUpdated())
    }
  }

  // La etiqueta llega como descriptor y se formatea aqui, para que la frase
  // completa se pueda reordenar en cada idioma.
  const handleCopy = async (value: string, labelMsg: MessageDescriptor) => {
    if (!value) return
    const label = intl.formatMessage(labelMsg)

    if (!navigator?.clipboard?.writeText) {
      toast.info(intl.formatMessage(S.toastCopyManually, {label}))
      return
    }

    try {
      await navigator.clipboard.writeText(value)
      toast.success(intl.formatMessage(S.toastCopied, {label}))
    } catch (_) {
      toast.error(intl.formatMessage(S.toastCopyFailed, {label}))
    }
  }

  const handleStartTwoFactorSetup = async () => {
    try {
      const response = await gqlStartTwoFactorSetup()
      const result = response.data?.startTwoFactorSetup

      if (!result?.success) {
        toast.error(result?.message || intl.formatMessage(S.toastSetupStartFailed))
        return
      }

      setTwoFactorSetupKey(result.manualEntryKey || null)
      setTwoFactorSetupUrl(result.otpauthUrl || null)
      setTwoFactorSetupQrSvg(result.qrCodeSvg || null)
      setTwoFactorSetupCode('')
      toast.success(result.message || intl.formatMessage(S.toastSetupStarted))
    } catch (error: any) {
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastSetupStartFailed)))
    }
  }

  const handleConfirmTwoFactorSetup = async () => {
    if (!twoFactorSetupCode.trim()) {
      toast.error(intl.formatMessage(S.toastCodeRequired))
      return
    }

    try {
      const response = await gqlConfirmTwoFactorSetup({
        variables: {
          code: twoFactorSetupCode.trim(),
        },
      })
      const result = response.data?.confirmTwoFactorSetup

      if (!result?.success) {
        toast.error(result?.message || intl.formatMessage(S.toastConfirmFailed))
        return
      }

      toast.success(result.message || intl.formatMessage(S.toastEnabled))
      setTwoFactorSetupKey(null)
      setTwoFactorSetupUrl(null)
      setTwoFactorSetupQrSvg(null)
      setTwoFactorSetupCode('')
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastConfirmFailed)))
    }
  }

  const handleDisableTwoFactor = async () => {
    if (!disableTwoFactorPassword.trim()) {
      toast.error(intl.formatMessage(S.toastPasswordRequired))
      return
    }

    if (!disableTwoFactorCode.trim()) {
      toast.error(intl.formatMessage(S.toastAuthCodeRequired))
      return
    }

    try {
      const response = await gqlDisableTwoFactor({
        variables: {
          password: disableTwoFactorPassword.trim(),
          code: disableTwoFactorCode.trim(),
        },
      })
      const result = response.data?.disableTwoFactor

      if (!result?.success) {
        toast.error(result?.message || intl.formatMessage(S.toastDisableFailed))
        return
      }

      toast.success(result.message || intl.formatMessage(S.toastDisabled))
      setDisableTwoFactorPassword('')
      setDisableTwoFactorCode('')
      setTwoFactorSetupKey(null)
      setTwoFactorSetupUrl(null)
      setTwoFactorSetupQrSvg(null)
      setTwoFactorSetupCode('')
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastDisableFailed)))
    }
  }

  const handleResetTwoFactor = async () => {
    if (!targetUserId) return

    try {
      const response = await gqlResetTwoFactor({
        variables: {
          userId: targetUserId,
        },
      })

      const result = response.data?.resetUserTwoFactorMutation
      if (!result?.success) {
        const message = result?.errors?.[0]?.message || 'Unable to reset two-factor authentication.'
        toast.error(message)
        return
      }

      toast.success(intl.formatMessage(S.toastResetDone))
      await runUpdated()
    } catch (error: any) {
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastResetFailed)))
    }
  }

  const handleToggleEmailFallback = async (enabled: boolean) => {
    if (!targetUserId) return

    const previousValue = adminEmailFallbackEnabled
    setAdminEmailFallbackEnabled(enabled)

    try {
      const response = await gqlSetUserTwoFactorEmailFallback({
        variables: {
          userId: targetUserId,
          enabled,
        },
      })

      const result = response.data?.setUserTwoFactorEmailFallbackMutation
      if (!result?.success) {
        setAdminEmailFallbackEnabled(previousValue)
        const message =
          result?.errors?.[0]?.message || intl.formatMessage(S.toastFallbackFailed)
        toast.error(message)
        return
      }

      setAdminEmailFallbackEnabled(Boolean(result.twoFactorEmailFallbackEnabled))
      toast.success(
        intl.formatMessage(enabled ? S.toastFallbackEnabled : S.toastFallbackDisabled)
      )
      await runUpdated()
    } catch (error: any) {
      setAdminEmailFallbackEnabled(previousValue)
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastFallbackFailed)))
    }
  }

  const handleToggleTwoFactorRequired = async (enabled: boolean) => {
    if (!targetUserId) return

    const previousValue = adminTwoFactorRequired
    setAdminTwoFactorRequired(enabled)

    try {
      const response = await gqlSetUserTwoFactorRequired({
        variables: {
          userId: targetUserId,
          enabled,
        },
      })

      const result = response.data?.setUserTwoFactorRequiredMutation
      if (!result?.success) {
        setAdminTwoFactorRequired(previousValue)
        const message =
          result?.errors?.[0]?.message || intl.formatMessage(S.toastRequiredFailed)
        toast.error(message)
        return
      }

      setAdminTwoFactorRequired(Boolean(result.twoFactorRequired))
      toast.success(intl.formatMessage(enabled ? S.toastRequiredEnabled : S.toastRequiredRemoved))
      await runUpdated()
    } catch (error: any) {
      setAdminTwoFactorRequired(previousValue)
      toast.error(getErrorMessage(error, intl.formatMessage(S.toastRequiredFailed)))
    }
  }

  const hasAdminPolicyAccess = !canManageOwnTwoFactor && canResetTwoFactor && !!targetUserId
  const showResetAction = hasAdminPolicyAccess && twoFactorEnabled
  const showAdminRequiredToggle = hasAdminPolicyAccess && showAdminRequiredControl
  const showAdminEmailFallbackToggle = hasAdminPolicyAccess
  const effectiveTwoFactorRequired = showAdminRequiredToggle
    ? adminTwoFactorRequired
    : Boolean(twoFactorRequired)
  const effectiveEmailFallbackEnabled = showAdminEmailFallbackToggle
    ? adminEmailFallbackEnabled
    : Boolean(twoFactorEmailFallbackEnabled)
  const canDisableTwoFactor = canManageOwnTwoFactor && twoFactorEnabled && !effectiveTwoFactorRequired

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-6'>
        <div>
          <div className='d-flex align-items-center gap-3 mb-2 flex-wrap'>
            <span className={`badge badge-light-${twoFactorEnabled ? 'success' : 'secondary'}`}>
              {twoFactorEnabled ? (
                <FormattedMessage {...S.twoFaEnabled} />
              ) : (
                <FormattedMessage {...S.twoFaDisabled} />
              )}
            </span>
            {effectiveTwoFactorRequired && (
              <span className='badge badge-light-warning'>
                <FormattedMessage {...S.twoFaRequiredBadge} />
              </span>
            )}
            {twoFactorEnabled && effectiveEmailFallbackEnabled && (
              <span className='text-muted fs-7'>
                <FormattedMessage {...S.twoFaFallbackOn} />
              </span>
            )}
          </div>
          <div className='text-gray-600 fs-6'>
            <FormattedMessage {...S.twoFaIntro} />
          </div>
        </div>

        {canManageOwnTwoFactor && !twoFactorEnabled && !twoFactorSetupKey && (
          <Button
            type='button'
            className='btn btn-primary'
            onClick={handleStartTwoFactorSetup}
            disabled={loadingStartTwoFactorSetup}
          >
            {!loadingStartTwoFactorSetup && <FormattedMessage {...S.twoFaEnableButton} />}
            {loadingStartTwoFactorSetup && (
              <span className='indicator-progress' style={{display: 'block'}}>
                <FormattedMessage id='admin.oneUser.pleaseWait' defaultMessage='Please wait...' />
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        )}

        {showResetAction && (
          <Button
            type='button'
            className='btn btn-light-danger'
            onClick={handleResetTwoFactor}
            disabled={loadingResetTwoFactor}
          >
            {!loadingResetTwoFactor && <FormattedMessage {...S.twoFaResetButton} />}
            {loadingResetTwoFactor && (
              <span className='indicator-progress' style={{display: 'block'}}>
                <FormattedMessage id='admin.oneUser.pleaseWait' defaultMessage='Please wait...' />
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </Button>
        )}
      </div>

      {showAdminRequiredToggle && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.twoFaRequirementTitle} />
          </div>
          <div className='text-muted mb-4'>
            <FormattedMessage {...S.twoFaRequirementIntro} />
          </div>

          <div className='form-check form-switch form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`two_factor_required_${String(targetUserId)}`}
              checked={effectiveTwoFactorRequired}
              disabled={loadingSetUserTwoFactorRequired}
              onChange={(e) => handleToggleTwoFactorRequired(e.target.checked)}
            />
            <label
              className='form-check-label fw-semibold'
              htmlFor={`two_factor_required_${String(targetUserId)}`}
            >
              <FormattedMessage {...S.twoFaRequireSetup} />
            </label>
          </div>

          <div className='text-muted fs-7 mt-3'>
            {twoFactorEnabled ? (
              <FormattedMessage {...S.twoFaRequireHintOn} />
            ) : (
              <FormattedMessage {...S.twoFaRequireHintOff} />
            )}
          </div>
        </div>
      )}

      {showAdminEmailFallbackToggle && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.twoFaFallbackTitle} />
          </div>
          <div className='text-muted mb-4'>
            <FormattedMessage {...S.twoFaFallbackIntro} />
          </div>

          <div className='form-check form-switch form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`two_factor_email_fallback_${String(targetUserId)}`}
              checked={effectiveEmailFallbackEnabled}
              disabled={loadingSetUserTwoFactorEmailFallback}
              onChange={(e) => handleToggleEmailFallback(e.target.checked)}
            />
            <label
              className='form-check-label fw-semibold'
              htmlFor={`two_factor_email_fallback_${String(targetUserId)}`}
            >
              <FormattedMessage {...S.twoFaAllowFallback} />
            </label>
          </div>

          <div className='text-muted fs-7 mt-3'>
            {twoFactorEnabled ? (
              <FormattedMessage {...S.twoFaFallbackHintOn} />
            ) : (
              <FormattedMessage {...S.twoFaFallbackHintOff} />
            )}
          </div>
        </div>
      )}

      {canManageOwnTwoFactor && effectiveTwoFactorRequired && !twoFactorEnabled && (
        <div className='alert alert-warning mb-6'>
          <FormattedMessage {...S.twoFaRequiredWarning} />
        </div>
      )}

      {canManageOwnTwoFactor && !twoFactorEnabled && twoFactorSetupKey && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.twoFaSetupTitle} />
          </div>
          <div className='text-muted mb-4'>
            <FormattedMessage {...S.twoFaSetupIntro} />
          </div>

          <Row className='g-4'>
            <Col md={5} sm={12}>
              <div className='d-flex flex-column align-items-center justify-content-center h-100'>
                {twoFactorSetupQrSvg ? (
                  <div
                    className='bg-white border rounded p-3'
                    style={{maxWidth: '220px'}}
                    dangerouslySetInnerHTML={{__html: twoFactorSetupQrSvg}}
                  />
                ) : (
                  <div className='text-muted fs-7'>
                    <FormattedMessage {...S.twoFaQrUnavailable} />
                  </div>
                )}
                <div className='text-muted fs-8 mt-3 text-center'>
                  <FormattedMessage {...S.twoFaScanHint} />
                </div>
              </div>
            </Col>

            <Col md={7} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage {...S.twoFaSetupCodeLabel} />
                </Form.Label>
                <InputGroup>
                  <Form.Control readOnly value={twoFactorSetupKey} />
                  <Button
                    type='button'
                    variant='light-primary'
                    onClick={() => handleCopy(twoFactorSetupKey, S.labelSetupCode)}
                  >
                    <FormattedMessage id='common.action.copy' defaultMessage='Copy' />
                  </Button>
                </InputGroup>
                <Form.Text className='text-muted'>
                  <FormattedMessage {...S.twoFaSetupCodeHint} />
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={12} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage {...S.twoFaVerificationCode} />
                </Form.Label>
                <Form.Control
                  value={twoFactorSetupCode}
                  onChange={(e) =>
                    setTwoFactorSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder='000000'
                  inputMode='numeric'
                />
              </Form.Group>
            </Col>
          </Row>

          {twoFactorSetupUrl && (
            <details className='mt-4'>
              <summary className='text-primary fw-semibold'>
                <FormattedMessage {...S.twoFaAdvancedLink} />
              </summary>
              <div className='mt-3'>
                <InputGroup>
                  <Form.Control readOnly value={twoFactorSetupUrl} />
                  <Button
                    type='button'
                    variant='light-primary'
                    onClick={() => handleCopy(twoFactorSetupUrl, S.labelSetupLink)}
                  >
                    <FormattedMessage id='common.action.copy' defaultMessage='Copy' />
                  </Button>
                </InputGroup>
              </div>
            </details>
          )}

          <div className='d-flex gap-3 flex-wrap mt-5'>
            <Button
              type='button'
              className='btn btn-success'
              onClick={handleConfirmTwoFactorSetup}
              disabled={loadingConfirmTwoFactorSetup}
            >
              {!loadingConfirmTwoFactorSetup && <FormattedMessage {...S.twoFaConfirmEnable} />}
              {loadingConfirmTwoFactorSetup && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <FormattedMessage id='admin.oneUser.pleaseWait' defaultMessage='Please wait...' />
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>

            <Button
              type='button'
              variant='light'
              onClick={() => {
                setTwoFactorSetupKey(null)
                setTwoFactorSetupUrl(null)
                setTwoFactorSetupQrSvg(null)
                setTwoFactorSetupCode('')
              }}
            >
              <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
            </Button>
          </div>
        </div>
      )}

      {canDisableTwoFactor && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.twoFaDisableTitle} />
          </div>
          <div className='text-muted mb-4'>
            <FormattedMessage {...S.twoFaDisableIntro} />
          </div>

          <Row className='g-4'>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage {...S.twoFaCurrentPassword} />
                </Form.Label>
                <Form.Control
                  type='password'
                  value={disableTwoFactorPassword}
                  onChange={(e) => setDisableTwoFactorPassword(e.target.value)}
                  placeholder={intl.formatMessage(S.twoFaCurrentPassword)}
                />
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage {...S.twoFaAuthenticatorCode} />
                </Form.Label>
                <Form.Control
                  value={disableTwoFactorCode}
                  onChange={(e) =>
                    setDisableTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder='000000'
                  inputMode='numeric'
                />
              </Form.Group>
            </Col>
          </Row>

          <div className='mt-5'>
            <Button
              type='button'
              className='btn btn-light-danger'
              onClick={handleDisableTwoFactor}
              disabled={loadingDisableTwoFactor}
            >
              {!loadingDisableTwoFactor && <FormattedMessage {...S.twoFaDisableButton} />}
              {loadingDisableTwoFactor && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <FormattedMessage id='admin.oneUser.pleaseWait' defaultMessage='Please wait...' />
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {canManageOwnTwoFactor && twoFactorEnabled && effectiveTwoFactorRequired && (
        <div className='border rounded p-5 bg-light'>
          <div className='fw-bold mb-2'>
            <FormattedMessage {...S.twoFaRequiredTitle} />
          </div>
          <div className='text-muted'>
            <FormattedMessage {...S.twoFaRequiredIntro} />
          </div>
        </div>
      )}

      {!canManageOwnTwoFactor && (
        <div className='text-muted fs-7'>
          <FormattedMessage {...S.twoFaOtherUserNote} />
        </div>
      )}
    </div>
  )
}
